import { GeiInventoryListItem, GeiInventoryVersion, GeiVersionState } from './types';

/** Same group enforced by backend lifecycle authorization and T11. */
export const GEI_LIFECYCLE_SUPER_ADMIN_GROUP_ID = 'ec438afc-63f2-4acf-b1d4-e51df3043190';

/** Administrator group already used by GEI inventory screens. Not a lifecycle role. */
export const GEI_ADMINISTRATOR_GROUP_ID = '1879ed5c-573a-44aa-848d-2f3b16a82bfc';

export function isGeiLifecycleAllowed(groupId: string | null | undefined): boolean {
    return (groupId || '').toLowerCase() === GEI_LIFECYCLE_SUPER_ADMIN_GROUP_ID;
}

/** Results organization picker: Super admin and Administrador, matched by group id from `rol`. */
export function isResultsOrganizationAdmin(groupId: string | null | undefined): boolean {
    const id = (groupId || '').toLowerCase();
    return id === GEI_LIFECYCLE_SUPER_ADMIN_GROUP_ID || id === GEI_ADMINISTRATOR_GROUP_ID;
}

/** Badge text. Comparisons and payloads stay OPEN / CLOSED. */
export function formatVersionStateBadge(state: string | null | undefined): string {
    if (state === 'OPEN') return 'ABIERTO';
    if (state === 'CLOSED') return 'CERRADO';
    return '';
}

/** Sentence form: versión ABIERTA / versión CERRADA. */
export function formatVersionStateSentence(state: string | null | undefined): string {
    if (state === 'OPEN') return 'ABIERTA';
    if (state === 'CLOSED') return 'CERRADA';
    return '';
}

/**
 * `GET api/results/years` returns `{ id, year }`. Results and the version
 * selector both need the calendar year, not the inventory id.
 */
export function resultsYearSelectValue(entry: unknown): string {
    if (entry && typeof entry === 'object' && 'year' in entry) {
        const year = (entry as { year?: unknown }).year;
        return year === undefined || year === null ? '' : String(year);
    }
    return entry === undefined || entry === null ? '' : String(entry);
}

export function buildGeiResultsYearUrl(organizationId: string, year: string, versionId?: string): string {
    const base = `/api/results/organization/years/${organizationId}/${year}?typeResult=GEI`;
    if (!versionId) return base;
    return `${base}&versionId=${encodeURIComponent(versionId)}`;
}

/** Date line for the version drawer. A blank or missing name keeps the date only and never prints an id. */
export function formatVersionEventLine(label: 'Creada' | 'Cerrada', whenText: string, actorName?: string | null): string {
    const name = (actorName || '').trim();
    if (!name) return `${label}: ${whenText}`;
    return `${label}: ${whenText}, por ${name}`;
}

export function formatVersionLabel(versionNumber: number, label: string | null | undefined): string {
    const trimmed = (label || '').trim();
    if (trimmed.length > 0) {
        return `V${versionNumber} · ${trimmed}`;
    }
    return `Versión ${versionNumber}`;
}

export function suggestedNextLabel(nextVersionNumber: number): string {
    if (nextVersionNumber === 2) return 'Ajustes';
    if (nextVersionNumber === 3) return 'Final';
    return `Versión ${nextVersionNumber}`;
}

export function isClosedVersion(state: string | null | undefined): boolean {
    return state === 'CLOSED';
}

export interface LifecycleActionVisibility {
    showClose: boolean;
    showCreateNext: boolean;
    showCreateCorrection: boolean;
    creationBlockedReason: string | null;
}

export function lifecycleActionVisibility(
    isSuperAdmin: boolean,
    state: GeiVersionState | string,
    openVersionExists: boolean
): LifecycleActionVisibility {
    if (!isSuperAdmin) {
        return {
            showClose: false,
            showCreateNext: false,
            showCreateCorrection: false,
            creationBlockedReason: null
        };
    }
    if (state === 'OPEN') {
        return {
            showClose: true,
            showCreateNext: false,
            showCreateCorrection: false,
            creationBlockedReason: null
        };
    }
    if (openVersionExists) {
        return {
            showClose: false,
            showCreateNext: false,
            showCreateCorrection: false,
            creationBlockedReason: 'Ya existe una versión ABIERTA. Ciérrela antes de crear otra.'
        };
    }
    return {
        showClose: false,
        showCreateNext: true,
        showCreateCorrection: true,
        creationBlockedReason: null
    };
}

export function versionIdForResultsRequest(selected: string | null | undefined): string | undefined {
    if (!selected || selected === 'current') return undefined;
    return selected;
}

export function shouldResetVersionOnYearChange(previousYear: string, nextYear: string): boolean {
    return previousYear !== nextYear;
}

export interface ResultsVersionOption {
    inventoryId: string;
    inventoryLabel: string;
    versionId: string;
    label: string;
    state: GeiVersionState;
    isCurrent: boolean;
    createdAt: string;
}

export interface ResultsVersionGroups {
    multipleInventories: boolean;
    groups: { inventoryId: string; inventoryLabel: string; versions: ResultsVersionOption[] }[];
}

export const CONSOLIDATED_RESULTS_LABEL = 'Actual (consolidado)';

export type OperationalVersionLoad = 'loading' | 'error' | 'ready';

/** Mutations render only after a successful OPEN resolution. Loading and errors stay closed. */
export function canMutateOperationalVersion(load: OperationalVersionLoad, state: string | null | undefined): boolean {
    return load === 'ready' && state === 'OPEN';
}

export function resolveOperationalVersion(
    versions: GeiInventoryVersion[] | null,
    explicitVersionId: string | null
): { load: OperationalVersionLoad; version: GeiInventoryVersion | null } {
    if (!versions) return { load: 'error', version: null };
    const selected = explicitVersionId
        ? versions.find((version) => version.id === explicitVersionId) || null
        : versions.find((version) => version.isCurrent) || null;
    if (!selected) return { load: 'error', version: null };
    return { load: 'ready', version: selected };
}

export function formatResultsVersionOption(version: Pick<GeiInventoryVersion, 'versionNumber' | 'label' | 'isCurrent'>): string {
    const base = formatVersionLabel(version.versionNumber, version.label);
    return version.isCurrent ? `${base} (Actual)` : base;
}

export function sortVersionsByCreatedAtDesc(versions: GeiInventoryVersion[]): GeiInventoryVersion[] {
    return versions.slice().sort((left, right) => {
        const rightTime = new Date(right.createdAt).getTime();
        const leftTime = new Date(left.createdAt).getTime();
        const rightMs = Number.isNaN(rightTime) ? 0 : rightTime;
        const leftMs = Number.isNaN(leftTime) ? 0 : leftTime;
        if (rightMs !== leftMs) return rightMs - leftMs;
        return right.versionNumber - left.versionNumber;
    });
}

export interface ResultsDefaultSelection {
    versionId?: string;
    label: string;
    state?: GeiVersionState;
    consolidated: boolean;
}

export function defaultResultsSelection(catalog: ResultsVersionGroups): ResultsDefaultSelection {
    if (catalog.multipleInventories || catalog.groups.length !== 1) {
        return { label: CONSOLIDATED_RESULTS_LABEL, consolidated: true };
    }
    const current = catalog.groups[0].versions.find((version) => version.isCurrent);
    if (!current) {
        return { label: CONSOLIDATED_RESULTS_LABEL, consolidated: true };
    }
    return {
        versionId: current.versionId,
        label: current.label,
        state: current.state,
        consolidated: false
    };
}

export function inventoryDisplayName(inventory: GeiInventoryListItem): string {
    const description = (inventory.description || '').trim();
    if (description.length > 0) return description;
    const person = (inventory.personCharge || '').trim();
    if (person.length > 0) return `${inventory.year} · ${person}`;
    const org = (inventory.msidentityOrganizationName || '').trim();
    if (org.length > 0) return `${org} · ${inventory.year}`;
    return `Inventario ${inventory.year}`;
}

export function buildResultsVersionGroups(
    inventories: GeiInventoryListItem[],
    versionsByInventory: Record<string, GeiInventoryVersion[]>
): ResultsVersionGroups {
    const groups = inventories.map((inventory) => {
        const versions = sortVersionsByCreatedAtDesc(versionsByInventory[inventory.id] || [])
            .map((version) => ({
                inventoryId: inventory.id,
                inventoryLabel: inventoryDisplayName(inventory),
                versionId: version.id,
                label: formatResultsVersionOption(version),
                state: version.state,
                isCurrent: version.isCurrent,
                createdAt: version.createdAt
            }));
        return {
            inventoryId: inventory.id,
            inventoryLabel: inventoryDisplayName(inventory),
            versions
        };
    }).filter((group) => group.versions.length > 0);

    return {
        multipleInventories: groups.length > 1,
        groups
    };
}

export function lifecycleErrorCode(body: unknown): string {
    if (!body || typeof body !== 'object') return '';
    const record = body as { error?: unknown; message?: unknown };
    if (typeof record.error === 'string' && record.error.indexOf('Exception') < 0) {
        return record.error;
    }
    if (typeof record.message === 'string') return record.message;
    if (Array.isArray(record.message) && typeof record.message[0] === 'string') {
        return record.message[0];
    }
    return '';
}

export function lifecycleErrorShouldRefresh(status: number, code: string): boolean {
    return status === 409 || code === 'VERSION_ALREADY_CLOSED' || code === 'OPEN_VERSION_ALREADY_EXISTS';
}

export function lifecycleErrorMessage(status: number, code: string): string {
    if (code === 'LIFECYCLE_UNAUTHORIZED' || status === 403) {
        return 'No tiene permiso para cambiar el ciclo de vida de la versión.';
    }
    if (status === 404 || code === 'VERSION_NOT_FOUND') {
        return 'La versión o el inventario ya no está disponible.';
    }
    if (code === 'VERSION_ALREADY_CLOSED') {
        return 'La versión ya estaba cerrada. Se actualizó el historial.';
    }
    if (code === 'OPEN_VERSION_ALREADY_EXISTS') {
        return 'Ya existe una versión abierta para este inventario.';
    }
    if (code === 'CORRECTION_REASON_REQUIRED') {
        return 'Indique el motivo de la corrección.';
    }
    if (code === 'INVALID_PREDECESSOR') {
        return 'Solo se puede crear una versión desde una versión cerrada.';
    }
    return 'No se pudo completar la operación de versión.';
}

export function readVersionIdFromSearch(search: string): string | null {
    const params = new URLSearchParams(search.startsWith('?') ? search.substring(1) : search);
    const value = params.get('versionId');
    if (!value || value.trim().length === 0) return null;
    return value;
}

export function withVersionQuery(path: string, versionId: string | null): string {
    if (!versionId) return path;
    const joiner = path.indexOf('?') >= 0 ? '&' : '?';
    return `${path}${joiner}versionId=${encodeURIComponent(versionId)}`;
}
